import { voiceFor, type Locale } from '@chessmate/persona';
import { api } from './api.js';
import { effectiveAudio, useAudioSettingsStore } from '../state/audio-settings-store.js';

/** What `speak()` can hand back. `off` means the device is muted or voices are locked off. */
export type SpeakResult = 'ok' | 'unavailable' | 'off';

/** The audio element the speech is played through (the browser's `Audio`, or a test seam). */
export interface SpeechAudio {
  play(): Promise<void>;
  /** The element's own volume, 0..1. A seam may leave it out. */
  volume?: number;
}

export interface SpeechOptions {
  readonly token: string;
  readonly voice?: string;
  /** Called with a short reason whenever a line could not be spoken (also logged to the console). */
  readonly onFailure?: (reason: string) => void;
  /** Test seam — loads the pre-generated clip index (defaults to fetching /tts/index.json). */
  readonly loadClipIndex?: () => Promise<ClipIndex | null>;
  /** Test seam — defaults to the browser Audio. */
  readonly audioFactory?: (url: string) => SpeechAudio;
}

/**
 * The level a spoken line is played at.
 *
 * The TTS path plays through an `<audio>` element, NOT the Web Audio mixer, so the mixer's buses
 * do not reach it: the voice volume and the master volume (the cap a grown-up sets) are applied
 * here, on the element. `0` means nothing should be heard.
 */
function speechVolume(): number {
  const eff = effectiveAudio(useAudioSettingsStore.getState());
  if (eff.muted || !eff.voiceOn) return 0;
  return Math.min(1, eff.voice * eff.master);
}

/**
 * The pre-generated clip index written by scripts/pregenerate-persona-lines.ts:
 * locale -> personaId -> authored text -> file (relative to /tts/).
 */
export interface ClipIndex {
  readonly version: number;
  readonly clips: Readonly<Record<string, Readonly<Record<string, Readonly<Record<string, string>>>>>>;
}

function fail(opts: SpeechOptions, reason: string, err?: unknown): 'unavailable' {
  console.warn(`[speech] ${reason}`, err ?? '');
  opts.onFailure?.(reason);
  return 'unavailable';
}

/** Play a ready URL at the settings volume. Returns 'ok' or 'unavailable' (reported, not swallowed). */
async function playUrl(url: string, volume: number, opts: SpeechOptions): Promise<SpeakResult> {
  const audio = opts.audioFactory ? opts.audioFactory(url) : new Audio(url);
  if (audio.volume !== undefined) audio.volume = volume;
  try {
    await audio.play();
  } catch (err) {
    return fail(opts, 'playback failed', err);
  }
  return 'ok';
}

/** Synthesize text via the API and play it. Returns 'ok', 'unavailable' or 'off'. */
export async function speak(text: string, opts: SpeechOptions): Promise<SpeakResult> {
  const volume = speechVolume();
  if (volume <= 0) return 'off';
  const voice = opts.voice ?? useAudioSettingsStore.getState().voice;
  let url: string;
  try {
    ({ url } = await api.tts(opts.token, text, voice));
  } catch (err) {
    return fail(opts, 'synthesis failed', err);
  }
  return playUrl(url, volume, opts);
}

let clipIndexPromise: Promise<ClipIndex | null> | null = null;

async function fetchClipIndex(): Promise<ClipIndex | null> {
  try {
    const res = await fetch('/tts/index.json');
    if (!res.ok) return null;
    return (await res.json()) as ClipIndex;
  } catch {
    return null;
  }
}

/** Forget the cached clip index (tests, or after a deploy swaps the clips). */
export function resetClipIndex(): void {
  clipIndexPromise = null;
}

/**
 * Speak an authored line in a persona's own voice. Chain: pre-generated clip (when the index has
 * this exact text) -> `/api/coach/tts` with the persona's voice. Honours mute, voices-off and the
 * parent locks exactly like `speak`. Text is always shown by the caller; this only adds sound.
 */
export async function speakAs(
  personaId: string,
  text: string,
  locale: Locale,
  opts: SpeechOptions,
): Promise<SpeakResult> {
  const volume = speechVolume();
  if (volume <= 0) return 'off';
  const voice = voiceFor(personaId, locale);
  if (!voice) return fail(opts, `no voice for persona "${personaId}" (${locale})`);
  try {
    clipIndexPromise ??= (opts.loadClipIndex ?? fetchClipIndex)();
    const file = (await clipIndexPromise)?.clips[locale]?.[personaId]?.[text];
    if (file) {
      const result = await playUrl(`/tts/${file}`, volume, opts);
      if (result === 'ok') return result;
      // A broken clip falls through to server synthesis.
    }
  } catch (err) {
    clipIndexPromise = null;
    console.warn('[speech] clip index lookup failed', err);
  }
  return speak(text, { ...opts, voice });
}

/** Warm the TTS cache for upcoming text (fire-and-forget, never throws). */
export function prefetchTts(text: string, opts: SpeechOptions): void {
  // Nothing will be spoken, so do not spend the voice service's rate limit on it.
  if (effectiveAudio(useAudioSettingsStore.getState()).muted) return;
  const voice = opts.voice ?? useAudioSettingsStore.getState().voice;
  void api.tts(opts.token, text, voice).catch((err) => console.warn('[speech] prefetch failed', err));
}
